import { apiClient } from "./client";
import type { Paginated, PageQuery } from "@/types/common";
import type { Enrollment, EnrollmentCreateInput, Student, StudentCreateInput } from "@/types/student";
import type { Charge } from "@/types/finance";
import type { AttendanceRecord } from "@/types/group";
import type { NotificationItem } from "@/types/notification";

export interface StudentAttendanceRecord extends AttendanceRecord {
  lesson?: {
    date: string;
    canceled?: boolean;
    topic?: string | null;
    group?: { subject?: { name: string } };
  };
}

export interface StudentsQuery extends PageQuery {
  search?: string;
  subjectId?: string;
  groupId?: string;
  teacherId?: string;
  level?: string;
  source?: string;
  branchId?: string;
  status?: string;
}

export interface ImportPreview {
  importRunId: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: Array<{ row: number; field?: string; message: string }>;
}

export async function previewStudentsImport(file: File, branchId: string, mapping: Record<string, string>): Promise<ImportPreview> {
  const body = new FormData();
  body.append("file", file);
  body.append("branchId", branchId);
  body.append("mapping", JSON.stringify(mapping));
  const { data } = await apiClient.post<ImportPreview>("/students/import/preview", body);
  return data;
}

export async function confirmStudentsImport(importRunId: string, branchId: string): Promise<{ imported: number }> {
  const { data } = await apiClient.post<{ imported: number }>(`/students/import/${importRunId}/confirm`, { branchId });
  return data;
}

export async function fetchStudents(query: StudentsQuery): Promise<Paginated<Student>> {
  const { data } = await apiClient.get<Paginated<Student>>("/students", { params: query });
  return data;
}

export async function downloadStudentsXlsx(query: StudentsQuery): Promise<void> {
  const response = await apiClient.get("/students/export/xlsx", { params: query, responseType: "blob" });
  const url = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "proschool-students.xlsx";
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function fetchStudent(id: string): Promise<Student> {
  const { data } = await apiClient.get<Student>(`/students/${id}`);
  return data;
}

export async function createStudent(payload: StudentCreateInput): Promise<Student> {
  const { data } = await apiClient.post<Student>("/students", payload);
  return data;
}

export async function updateStudent(id: string, payload: Partial<StudentCreateInput>): Promise<Student> {
  const { data } = await apiClient.patch<Student>(`/students/${id}`, payload);
  return data;
}

export async function fetchStudentEnrollments(studentId: string): Promise<Enrollment[]> {
  const { data } = await apiClient.get<Enrollment[]>(`/students/${studentId}/enrollments`);
  return data;
}

export async function createStudentEnrollment(studentId: string, payload: EnrollmentCreateInput): Promise<Enrollment> {
  const { data } = await apiClient.post<Enrollment>(`/students/${studentId}/enrollments`, payload);
  return data;
}

export async function fetchStudentCharges(studentId: string): Promise<Charge[]> {
  const { data } = await apiClient.get<Charge[]>(`/students/${studentId}/charges`);
  return data;
}

export async function fetchStudentDebt(studentId: string): Promise<{ debt: number }> {
  const { data } = await apiClient.get<{ debt: number }>(`/students/${studentId}/debt`);
  return data;
}

export async function fetchStudentAttendance(studentId: string): Promise<StudentAttendanceRecord[]> {
  const { data } = await apiClient.get<StudentAttendanceRecord[]>(`/students/${studentId}/attendance`);
  return data;
}

export async function fetchStudentNotifications(studentId: string): Promise<NotificationItem[]> {
  const { data } = await apiClient.get<NotificationItem[]>(`/students/${studentId}/notifications`);
  return data;
}
